import { MouseEvent, useCallback, useRef } from "react"
import { Link } from "react-router-dom"
import { Subitem } from "../Subitem"
import { ChevronDownSvg } from "~/ui-rtk/components/ui/svg/arrow"
import { SessionRole } from "~/api/dto/auth/session-role"

import { cn } from "~/ui-rtk/utils/tailwind-utils"

import { useConnect } from "./connect"

import type { NavItem } from "../types"
import { TPosthogFeatureKey } from "~/ui-rtk/constants/feature-flags"
import usePostHogFeatureFlags from "~/ui-rtk/hooks/posthog-feaures"
import {
  TAnimatedSvgIconProps,
  TAnimatedSvgIconSize,
} from "~/ui-rtk/components/ui/common/Icons/IconType"
import { ForwardRefComponent } from "motion/react"

export type TSidebarItemProps = NavItem & {
  Icon: ForwardRefComponent<TAnimatedSvgIconProps, TAnimatedSvgIconSize>
  items?: NavItem[]
  labelVisible?: boolean
  sessionRole: SessionRole
  onBeforeNavigate?: () => boolean
  onNavigate?: () => void
  onClick?: () => void
  featureFlag?: TPosthogFeatureKey
  isHidden?: () => boolean
}

export const SidebarItem: React.FC<TSidebarItemProps> = ({
  label,
  path,
  availableTo,
  Icon,
  items,
  labelVisible,
  onBeforeNavigate,
  onNavigate,
  onClick,
  sessionRole,
  featureFlag,
  isHidden,
}) => {
  const iconRef = useRef<TAnimatedSvgIconProps>(null)

  const { isActive, isExpanded, handleClick, handleNavigate } = useConnect(
    path,
    onBeforeNavigate,
    onNavigate,
    items,
  )

  if (!availableTo?.includes(sessionRole)) {
    return null
  }

  const clickHandler = useCallback(
    (event: MouseEvent) => {
      if (onClick) {
        onClick()
      } else if (handleClick) {
        handleClick(event)
      }
    },
    [onClick, handleClick],
  )

  if (featureFlag && !usePostHogFeatureFlags(featureFlag)) {
    return null
  }

  if (isHidden && isHidden()) {
    return null
  }

  const hasSubitems = items && items.length > 0

  const sharedClassName =
    "group flex items-center justify-between rounded gap-2 cursor-pointer hover:bg-gray/[.1] px-3 py-2"
  const sharedEventHandlers = {
    onMouseEnter: () => {
      iconRef.current?.startAnimation()
    },
    onMouseLeave: () => {
      iconRef.current?.stopAnimation()
    },
  }

  const contentJSX = (
    <>
      <div className="flex gap-2">
        <Icon
          size={24}
          className={cn([
            "min-w-5.5",
            isActive
              ? "text-system-blue-100"
              : "text-system-blue-600 group-hover:text-platinum",
          ])}
          ref={iconRef}
        />
        <p
          className={cn(
            "transition-all duration-300 font-medium whitespace-nowrap",
            labelVisible ? "opacity-100 max-w-full" : "opacity-0 max-w-0 ml-0",
            isActive ? "text-platinum" : "text-gray group-hover:text-platinum",
          )}
        >
          {label}
        </p>
      </div>
      {labelVisible && hasSubitems ? (
        <ChevronDownSvg
          size={16}
          fill={isActive ? "platinum" : "gray"}
          className={cn(
            "transition-all group-hover:fill-platinum",
            isExpanded && "rotate-180",
          )}
        />
      ) : null}
    </>
  )

  return (
    <div className="relative">
      {hasSubitems ? (
        <div
          onClick={clickHandler}
          className={sharedClassName}
          {...sharedEventHandlers}
        >
          {contentJSX}
        </div>
      ) : (
        <Link
          to={path || "#"}
          onClick={(e: MouseEvent) => {
            // Allow default Link behavior for standard clicks
            // Only prevent default for custom onClick handlers
            if (onClick) {
              e.preventDefault()
              onClick()
            } else {
              onNavigate?.()
            }
          }}
          className={sharedClassName}
          {...sharedEventHandlers}
        >
          {contentJSX}
        </Link>
      )}

      {hasSubitems ? (
        <ul
          className={cn(
            "flex flex-col gap-1 mt-2 ml-6",
            !(isExpanded && labelVisible) && "hidden",
          )}
        >
          {items?.map(item => (
            <Subitem
              key={item.path}
              onClick={handleNavigate}
              sessionRole={sessionRole}
              {...item}
            />
          ))}
        </ul>
      ) : null}
    </div>
  )
}
